import { sectionRenderer } from '@theme/section-renderer';
import { Component } from '@theme/component';
import { getMainProductGrid } from '@theme/navigation-ux';
import { forceLoadImages, shimmerUntilImagesLoaded } from '@theme/tab-panel-images';
import { debounce, requestIdleCallback, startViewTransition } from '@theme/utilities';

/**
 * Collection paths carry a locale prefix on non-primary markets
 * (`/es/collections/...`), so a plain `/collections/` prefix check misses them
 * and the click falls through to a full page load.
 * @param {URL} url
 * @returns {boolean}
 */
function isCollectionUrl(url) {
  return /^(\/[a-z]{2}(-[a-z]{2})?)?\/collections\//i.test(url.pathname);
}

class CollectionChildLinksComponent extends Component {
  #navigationGeneration = 0;

  connectedCallback() {
    super.connectedCallback();
    this.removeAttribute('data-not-ready');
    this.addEventListener('click', this.#handleClick);
    this.addEventListener('pointerenter', this.#handleLinkIntent, true);
    this.addEventListener('touchstart', this.#handleLinkIntent, { capture: true, passive: true });
    this.addEventListener('pointerleave', this.#cancelPrefetch, true);
    window.addEventListener('popstate', this.#handlePopState);
    this.querySelector('.collection-child-links__link.is-active')?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    forceLoadImages(this.#productGrid);
    this.#prefetchAllSiblings();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('pointerenter', this.#handleLinkIntent, true);
    this.removeEventListener('touchstart', this.#handleLinkIntent, { capture: true, passive: true });
    this.removeEventListener('pointerleave', this.#cancelPrefetch, true);
    window.removeEventListener('popstate', this.#handlePopState);
  }

  get #scroller() {
    return this.querySelector('.collection-child-links__scroller');
  }

  get #productGrid() {
    return getMainProductGrid();
  }

  get #productGridSectionId() {
    return this.#productGrid?.closest('.shopify-section')?.id?.replace('shopify-section-', '') ?? null;
  }

  /**
   * @returns {URL[]}
   */
  #getSiblingUrls() {
    return [...this.querySelectorAll('.collection-child-links__link')]
      .filter((link) => link instanceof HTMLAnchorElement && !link.classList.contains('is-active'))
      .map((link) => new URL(link.href));
  }

  /**
   * @param {URL[]} urls
   * @returns {URL[]}
   */
  #prioritizeAdjacent(urls) {
    const allLinks = [...this.querySelectorAll('.collection-child-links__link')];
    const activeIndex = allLinks.findIndex((link) => link.classList.contains('is-active'));
    const pathToUrl = new Map(urls.map((url) => [url.pathname, url]));
    /** @type {URL[]} */
    const ordered = [];

    /**
     * @param {number} offset
     */
    const addByOffset = (offset) => {
      const link = allLinks[activeIndex + offset];
      if (!(link instanceof HTMLAnchorElement)) return;

      const url = pathToUrl.get(new URL(link.href).pathname);
      if (url && !ordered.includes(url)) {
        ordered.push(url);
      }
    };

    addByOffset(1);
    addByOffset(-1);

    for (const url of urls) {
      if (!ordered.includes(url)) {
        ordered.push(url);
      }
    }

    return ordered;
  }

  #prefetchAllSiblings() {
    const urls = this.#getSiblingUrls();
    const productGridSectionId = this.#productGridSectionId;
    if (!productGridSectionId || !urls.length) return;

    const ordered = this.#prioritizeAdjacent(urls);

    /**
     * @param {number} index
     */
    const prefetchNext = (index) => {
      if (index >= ordered.length) return;

      sectionRenderer
        .getSectionHTML(productGridSectionId, true, ordered[index])
        .finally(() => prefetchNext(index + 1));
    };

    requestIdleCallback(() => prefetchNext(0), { timeout: 3000 });
  }

  /**
   * @param {HTMLAnchorElement} link
   * @returns {HTMLAnchorElement | null}
   */
  #setActiveTab(link) {
    const previousActive =
      this.querySelector('.collection-child-links__link.is-active') ?? null;

    this.querySelectorAll('.collection-child-links__link.is-active').forEach((activeLink) => {
      activeLink.classList.remove('is-active');
      activeLink.removeAttribute('aria-current');
    });

    link.classList.add('is-active');
    link.setAttribute('aria-current', 'page');

    return previousActive instanceof HTMLAnchorElement ? previousActive : null;
  }

  /**
   * @param {URL} url
   */
  #syncActiveTabFromUrl(url) {
    for (const link of this.querySelectorAll('.collection-child-links__link')) {
      if (!(link instanceof HTMLAnchorElement)) continue;
      if (new URL(link.href).pathname === url.pathname) {
        this.#setActiveTab(link);
        return;
      }
    }
  }

  /**
   * @param {HTMLAnchorElement | null} link
   */
  #restoreActiveTab(link) {
    if (!(link instanceof HTMLAnchorElement)) return;

    this.querySelectorAll('.collection-child-links__link.is-active').forEach((activeLink) => {
      activeLink.classList.remove('is-active');
      activeLink.removeAttribute('aria-current');
    });

    link.classList.add('is-active');
    link.setAttribute('aria-current', 'page');
  }

  /**
   * @param {HTMLElement | null} grid
   * @param {boolean} loading
   */
  #setGridLoading(grid, loading) {
    if (!grid) return;

    const skeleton = grid.querySelector('.collection-grid-skeleton');

    if (loading) {
      grid.setAttribute('data-grid-loading', '');
      skeleton?.setAttribute('aria-hidden', 'false');
    } else {
      grid.removeAttribute('data-grid-loading');
      skeleton?.setAttribute('aria-hidden', 'true');
    }
  }

  /**
   * Immediate tab + grid feedback before section fetches.
   * @param {HTMLAnchorElement | null} link
   * @returns {HTMLAnchorElement | null}
   */
  #beginNavigationFeedback(link) {
    const previousActiveTab =
      link instanceof HTMLAnchorElement ? this.#setActiveTab(link) : null;

    this.setAttribute('loading', '');
    this.#setGridLoading(this.#productGrid, true);

    return previousActiveTab;
  }

  /**
   * @param {URL} url
   */
  #prefetchSection = debounce((url) => {
    const productGridSectionId = this.#productGridSectionId;
    if (!productGridSectionId) return;

    sectionRenderer.getSectionHTML(productGridSectionId, true, url);
  }, 200);

  #cancelPrefetch = () => {
    this.#prefetchSection.cancel();
  };

  /**
   * @param {Event} event
   */
  #handleLinkIntent = (event) => {
    const link = event.target.closest('a[href]');
    if (!(link instanceof HTMLAnchorElement)) return;
    if (link.classList.contains('is-active')) return;

    const url = new URL(link.href);
    if (url.origin !== window.location.origin) return;
    if (!isCollectionUrl(url)) return;

    this.#prefetchSection(url);
  };

  #handleClick = (event) => {
    const link = event.target.closest('a[href]');
    if (!link) return;

    const url = new URL(link.href);
    if (url.origin !== window.location.origin) return;
    if (!isCollectionUrl(url)) return;
    if (link.classList.contains('is-active')) return;

    event.preventDefault();

    const generation = ++this.#navigationGeneration;
    const previousActiveTab = this.#beginNavigationFeedback(link);

    requestAnimationFrame(() => {
      void this.#navigate(url, { pushState: true, previousActiveTab, generation });
    });
  };

  #handlePopState = () => {
    const generation = ++this.#navigationGeneration;

    requestAnimationFrame(() => {
      void this.#navigate(new URL(window.location.href), { pushState: false, generation });
    });
  };

  /**
   * @param {number} generation
   * @returns {boolean}
   */
  #isStaleNavigation(generation) {
    return generation !== this.#navigationGeneration;
  }

  /**
   * @param {URL} url
   * @param {{ pushState?: boolean, previousActiveTab?: HTMLAnchorElement | null, generation: number }} options
   */
  async #navigate(url, { pushState = true, previousActiveTab = null, generation }) {
    if (!this.hasAttribute('loading')) {
      this.#beginNavigationFeedback(null);
    }

    if (!pushState) {
      this.#syncActiveTabFromUrl(url);
    }

    if (pushState) {
      history.pushState({}, '', url);
    }

    const productGridSectionId = this.#productGridSectionId;
    const productGrid = this.#productGrid;

    try {
      if (productGridSectionId) {
        productGrid?.setAttribute('data-just-morphed', '');

        await startViewTransition(
          () => sectionRenderer.renderSection(productGridSectionId, { url: new URL(url), cache: true }),
          ['product-grid']
        );
      }
    } catch (error) {
      productGrid?.removeAttribute('data-just-morphed');

      if (this.#isStaleNavigation(generation)) return;

      this.removeAttribute('loading');
      this.#setGridLoading(productGrid, false);

      if (previousActiveTab instanceof HTMLAnchorElement) {
        this.#restoreActiveTab(previousActiveTab);
      }

      throw error;
    }

    if (this.#isStaleNavigation(generation)) return;

    this.removeAttribute('loading');
    this.#setGridLoading(productGrid, false);
    forceLoadImages(productGrid);
    shimmerUntilImagesLoaded(productGrid);
    window.dispatchEvent(new Event('resize'));

    this.#centerActiveLink();
  }

  /**
   * Smoothly scrolls the scroller so the active link is centered, clamped to
   * the scroller's natural scroll bounds (links at the edges stay at the edge).
   */
  #centerActiveLink() {
    const scroller = this.#scroller;
    const activeLink = this.querySelector('.collection-child-links__link.is-active');
    if (!scroller || !(activeLink instanceof HTMLElement)) return;

    const scrollerRect = scroller.getBoundingClientRect();
    const linkRect = activeLink.getBoundingClientRect();

    const linkCenter = linkRect.left + linkRect.width / 2;
    const scrollerCenter = scrollerRect.left + scrollerRect.width / 2;

    const targetScrollLeft = scroller.scrollLeft + (linkCenter - scrollerCenter);
    const maxScrollLeft = scroller.scrollWidth - scroller.clientWidth;
    const clamped = Math.max(0, Math.min(targetScrollLeft, maxScrollLeft));

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    scroller.scrollTo({ left: clamped, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
  }
}

if (!customElements.get('collection-child-links-component')) {
  customElements.define('collection-child-links-component', CollectionChildLinksComponent);
}
